import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/#webpage`,
      url: `${SITE_URL}/`,
      name: '岡山市の工務店｜外装・内装リフォーム40年の実績｜株式会社おかやま工務店',
      description:
        '岡山市で40年の実績を持つ株式会社おかやま工務店。外装工事・内装工事・リフォーム・メンテナンスまで幅広く対応。見積もり無料。岡山市北区を中心に倉敷市など近隣市町村に対応。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#business` },
      dateModified: new Date().toISOString().split('T')[0],
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'ホーム',
            item: `${SITE_URL}/`,
          },
        ],
      },
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'h2', '.speakable'],
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-home');
      if (el) el.remove();
    };
  }, []);

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const [contactFormData, setContactFormData] = useState({
    name: '',
    furigana: '',
    phone: '',
    email: '',
    postalCode: '',
    address: '',
    inquiryType: '',
    message: '',
    agree: false,
  });

  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactSubmitStatus, setContactSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setContactFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setContactFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactFormData.agree) return;
    if (contactFormData.message.length > 500) return;
    setIsContactSubmitting(true);
    setContactSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      formBody.append('お名前', contactFormData.name);
      formBody.append('フリガナ', contactFormData.furigana);
      formBody.append('電話番号', contactFormData.phone);
      formBody.append('email', contactFormData.email);
      formBody.append('郵便番号', contactFormData.postalCode);
      formBody.append('ご住所', contactFormData.address);
      formBody.append('お問い合わせ内容', contactFormData.inquiryType);
      formBody.append('ご相談内容', contactFormData.message);
      const response = await fetch('https://readdy.ai/api/form/d6juhafgl4s4nd8dm4j0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setContactSubmitStatus('success');
        setContactFormData({ name: '', furigana: '', phone: '', email: '', postalCode: '', address: '', inquiryType: '', message: '', agree: false });
      } else {
        setContactSubmitStatus('error');
      }
    } catch {
      setContactSubmitStatus('error');
    } finally {
      setIsContactSubmitting(false);
    }
  };

  const faqs = [
    {
      question: '見積もりは無料ですか？',
      answer: 'はい、見積もりは無料です。お気軽にお問い合わせください。現地調査も無料で行っております。',
    },
    {
      question: '岡山市以外でも対応していますか？',
      answer: '岡山市北区を中心に、倉敷市など近隣市町村にも対応しております。まずはお気軽にご相談ください。',
    },
    {
      question: '工事中の生活への影響はありますか？',
      answer: 'できる限りお客様の生活に支障が出ないよう配慮して工事を進めます。工事前に詳しいスケジュールをご説明いたします。',
    },
    {
      question: '急な雨漏りや破損にも対応できますか？',
      answer: 'はい、緊急対応も承っております。まずはお電話にてご連絡ください。できる限り迅速に対応いたします。',
    },
    {
      question: '工事後の保証はありますか？',
      answer: '工事内容によって異なりますが、施工後の保証をご用意しております。詳細はお問い合わせの際にご確認ください。',
    },
    {
      question: '古い家でも対応できますか？',
      answer: 'はい、築年数の古い住宅でも対応可能です。長年の経験を活かし、適切な工法でご対応いたします。',
    },
    {
      question: '支払い方法はどのようなものがありますか？',
      answer: '現金・銀行振込に対応しております。分割払いについてはご相談ください。',
    },
    {
      question: '工事の相談から完成までどのくらいかかりますか？',
      answer: '工事の規模によって異なりますが、小規模な修理であれば相談から数日以内に対応可能です。大規模なリフォームの場合は、ご相談の上スケジュールを決定いたします。',
    },
  ];

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="hero-section" className="relative h-screen min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/77d2522c5ae9ad06309a91a06a4ea64f.webp"
            alt="岡山市の工務店"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full pt-32">
          <div className="max-w-2xl">
            <h2 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              岡山市で40年培った<br />確かな技術
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-white mb-10 leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              住まいの困りごと、<br />大工で経験した豊富な知識で即対応
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:086-012-3456"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#ff6b35] text-white text-base sm:text-lg font-bold rounded-full hover:bg-[#ff8555] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill mr-2"></i>
                今すぐ電話で相談
              </a>
              <a
                href="#contact-section"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('contact-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white text-base sm:text-lg font-bold rounded-full hover:bg-white hover:text-[#1a1a2e] transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-mail-line mr-2"></i>
                お問い合わせフォーム
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Company Section */}
      <section className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <span className="inline-block px-4 py-2 border border-[#ff6b35] text-[#ff6b35] text-sm rounded-full mb-4 whitespace-nowrap">
              私たちについて
            </span>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              株式会社おかやま工務店とは
            </h3>
            <div className="w-24 h-1 bg-[#ff6b35] mx-auto rounded-full"></div>
          </div>

          <div className="text-center mb-12 lg:mb-16 max-w-3xl mx-auto">
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              このたびは、株式会社おかやま工務店のホームページをご覧いただき、<br />
              誠にありがとうございます。<br />
              私どもは岡山市を中心に、大工として40年以上にわたり<br />
              地域の皆様の住まいを守り続けてまいりました。<br />
              外装・内装を問わず、お客様の「困った」に誠実に向き合い、<br />
              丁寧な仕事でお応えすることをお約束いたします。<br />
              どうぞお気軽にご相談ください。
            </p>
          </div>

          <div className="flex flex-col gap-16 lg:gap-24">
            {/* Item 1: 写真左・テキスト右 */}
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
              <div className="w-full lg:w-1/2 rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/4214b3cee289b0f04cd4b092aa8f1d85.webp"
                  alt="40年の確かな技術"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block text-[#ff6b35] font-bold text-sm mb-3 tracking-widest">01</span>
                <h4 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#1a1a2e] mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  40年の確かな技術
                </h4>
                <div className="w-12 h-1 bg-[#ff6b35] rounded-full mb-6"></div>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  住宅の修理から内装工事まで、豊富な経験でどんな工事も対応可能です。長年培った技術力で、お客様の大切な住まいを守ります。現場に精通した職人だからこそできる、確かな品質と安心の施工をお届けします。
                </p>
              </div>
            </div>

            {/* Item 2: テキスト左・写真右 */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-16">
              <div className="w-full lg:w-1/2 rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/10af157e0c062c64d3840c17a1b6f7dc.webp"
                  alt="幅広い対応力"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block text-[#ff6b35] font-bold text-sm mb-3 tracking-widest">02</span>
                <h4 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#1a1a2e] mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  幅広い対応力
                </h4>
                <div className="w-12 h-1 bg-[#ff6b35] rounded-full mb-6"></div>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  外装工事、内装工事、リフォーム、メンテナンスまで幅広く対応。一つの窓口で住まいのあらゆるお困りごとを解決いたします。小さな修理から大規模なリフォームまで、どんなご要望にも柔軟にお応えします。
                </p>
              </div>
            </div>

            {/* Item 3: 写真左・テキスト右 */}
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
              <div className="w-full lg:w-1/2 rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/99b56a6a389b2e790a0724ae1842fed9.webp"
                  alt="地域密着の安心感"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block text-[#ff6b35] font-bold text-sm mb-3 tracking-widest">03</span>
                <h4 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#1a1a2e] mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  地域密着の安心感
                </h4>
                <div className="w-12 h-1 bg-[#ff6b35] rounded-full mb-6"></div>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  岡山市を中心に40年。地域に根ざした信頼と実績で、アフターフォローも万全。お客様との長いお付き合いを大切にしています。施工後も安心してお任せいただける、地元密着の工務店です。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-[#1a1a2e]">
        <div>
          {/* Section Title - 紺色背景 */}
          <div className="py-12 lg:py-16 px-6">
            <div className="max-w-[1400px] mx-auto text-center">
              <span className="inline-block px-4 py-2 border-2 border-white/30 text-white text-sm rounded-full mb-4 whitespace-nowrap">
                幅広く対応いたします
              </span>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                サービス内容
              </h3>
              <div className="w-24 h-1 bg-[#ff6b35] mx-auto rounded-full"></div>
            </div>
          </div>

          {/* Services Detail */}
          <div className="pb-16 lg:pb-24 px-0">
            <div className="max-w-[1200px] mx-auto px-6">
              <div className="space-y-12 lg:space-y-16">

                {/* 外装工事 */}
                <div className="bg-white flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
                  <div className="w-full lg:w-1/2 flex-shrink-0 border-l-8 border-[#1a1a2e]">
                    <div className="w-full h-full min-h-[300px] lg:min-h-[400px]">
                      <img
                        src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/3397de9d817c4c6113a42ff969ca7597.webp"
                        alt="外装工事"
                        className="w-full h-full object-cover object-top"
                        style={{ minHeight: '300px' }}
                      />
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2 p-8 lg:p-10">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      外装工事
                    </h2>
                    <p className="text-base text-gray-700 leading-relaxed mb-6">
                      外壁の修理・塗装から屋根の補修まで、住まいの外観を美しく保ちます。経年劣化による傷みや、台風などの自然災害による損傷にも迅速に対応。防水性能の向上や断熱効果の改善も行います。
                    </p>
                    <div className="mb-6">
                      <h4 className="text-base font-bold text-[#1a1a2e] mb-3">対応内容</h4>
                      <ul className="space-y-2">
                        {['外壁修理・塗装', '屋根修理・葺き替え', '雨樋の修理・交換', '防水工事'].map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-check-line text-[#1a1a2e] text-xl mr-2 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-4 p-5 bg-[#faf8f3] rounded-xl">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">料金目安</p>
                        <p className="text-lg font-bold text-[#1a1a2e]">50,000円〜</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">工事期間</p>
                        <p className="text-lg font-bold text-[#1a1a2e]">3日〜2週間</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 内装工事 */}
                <div className="bg-white flex flex-col lg:flex-row-reverse gap-8 lg:gap-12 items-center">
                  <div className="w-full lg:w-1/2 flex-shrink-0 border-r-8 border-[#1a1a2e]">
                    <div className="w-full h-full min-h-[300px] lg:min-h-[400px]">
                      <img
                        src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/c69bcfa419e7f59e859010c8ed9ce977.webp"
                        alt="内装工事"
                        className="w-full h-full object-cover object-top"
                        style={{ minHeight: '300px' }}
                      />
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2 p-8 lg:p-10">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      内装工事
                    </h2>
                    <p className="text-base text-gray-700 leading-relaxed mb-6">
                      床の張り替え、壁紙の交換、建具の修理など、室内の快適性を高める工事を行います。ライフスタイルの変化に合わせた間取り変更や、バリアフリー化にも対応。お客様のご要望に合わせた最適なプランをご提案します。
                    </p>
                    <div className="mb-6">
                      <h4 className="text-base font-bold text-[#1a1a2e] mb-3">対応内容</h4>
                      <ul className="space-y-2">
                        {['床材の張り替え', '壁紙・クロスの交換', '建具の修理・交換', '間仕切り工事'].map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-check-line text-[#1a1a2e] text-xl mr-2 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-4 p-5 bg-[#faf8f3] rounded-xl">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">料金目安</p>
                        <p className="text-lg font-bold text-[#1a1a2e]">30,000円〜</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">工事期間</p>
                        <p className="text-lg font-bold text-[#1a1a2e]">2日〜1週間</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* リフォーム */}
                <div className="bg-white flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
                  <div className="w-full lg:w-1/2 flex-shrink-0 border-l-8 border-[#1a1a2e]">
                    <div className="w-full h-full min-h-[300px] lg:min-h-[400px]">
                      <img
                        src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/41e0560b188fc95be16311c4f7231363.webp"
                        alt="リフォーム"
                        className="w-full h-full object-cover object-top"
                        style={{ minHeight: '300px' }}
                      />
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2 p-8 lg:p-10">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      リフォーム
                    </h2>
                    <p className="text-base text-gray-700 leading-relaxed mb-6">
                      キッチン、浴室、トイレなどの水回りから、和室を洋室に変更するリフォームまで幅広く対応。古くなった設備を最新のものに交換し、快適で使いやすい空間を実現します。省エネ性能の向上も考慮した提案を行います。
                    </p>
                    <div className="mb-6">
                      <h4 className="text-base font-bold text-[#1a1a2e] mb-3">対応内容</h4>
                      <ul className="space-y-2">
                        {['キッチンリフォーム', '浴室・トイレリフォーム', '和室から洋室への変更', '増改築工事'].map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-check-line text-[#1a1a2e] text-xl mr-2 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-4 p-5 bg-[#faf8f3] rounded-xl">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">料金目安</p>
                        <p className="text-lg font-bold text-[#1a1a2e]">100,000円〜</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">工事期間</p>
                        <p className="text-lg font-bold text-[#1a1a2e]">1週間〜1ヶ月</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* メンテナンス */}
                <div className="bg-white flex flex-col lg:flex-row-reverse gap-8 lg:gap-12 items-center">
                  <div className="w-full lg:w-1/2 flex-shrink-0 border-r-8 border-[#1a1a2e]">
                    <div className="w-full h-full min-h-[300px] lg:min-h-[400px]">
                      <img
                        src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/ac8a33224c84b8951bc0cfffe0faad78.webp"
                        alt="メンテナンス"
                        className="w-full h-full object-cover object-top"
                        style={{ minHeight: '300px' }}
                      />
                    </div>
                  </div>
                  <div className="w-full lg:w-1/2 p-8 lg:p-10">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      メンテナンス
                    </h2>
                    <p className="text-base text-gray-700 leading-relaxed mb-6">
                      定期的な点検で住まいの問題を早期発見。雨漏りやシロアリ被害など、放置すると大きな被害につながる問題も、早期対応で最小限の費用で解決できます。長く安心して住み続けられる住まいづくりをサポートします。
                    </p>
                    <div className="mb-6">
                      <h4 className="text-base font-bold text-[#1a1a2e] mb-3">対応内容</h4>
                      <ul className="space-y-2">
                        {['定期点検サービス', '雨漏り修理', 'シロアリ対策', '緊急修理対応'].map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <i className="ri-check-line text-[#1a1a2e] text-xl mr-2 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-4 p-5 bg-[#faf8f3] rounded-xl">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">料金目安</p>
                        <p className="text-lg font-bold text-[#1a1a2e]">10,000円〜</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">工事期間</p>
                        <p className="text-lg font-bold text-[#1a1a2e]">即日〜3日</p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq-section" className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              よくある質問
            </h3>
            <div className="w-24 h-1 bg-[#ff6b35] mx-auto rounded-full"></div>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-[#ff6b35] transition-colors duration-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 lg:px-8 py-6 flex items-start justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start flex-1">
                    <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#ff6b35] text-white font-bold rounded-lg mr-4 text-lg">
                      Q
                    </span>
                    <h4 className="text-lg lg:text-xl font-bold text-[#1a1a2e] leading-relaxed pt-1">
                      {faq.question}
                    </h4>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-[#1a1a2e] ml-4 flex-shrink-0 transition-transform duration-300 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaqIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 lg:px-8 pb-6">
                    <div className="flex items-start pl-14">
                      <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#1a1a2e] text-white font-bold rounded-lg mr-4 text-lg">
                        A
                      </span>
                      <p className="text-base text-gray-700 leading-relaxed pt-2">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Overview Section - 会社概要 */}
      <section id="company-section" className="py-16 lg:py-24 px-6 bg-[#faf8f3]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              会社概要
            </h3>
            <div className="w-24 h-1 bg-[#ff6b35] mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column: Company Info Table */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h4 className="text-2xl lg:text-3xl font-black text-[#1a1a2e] mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                会社情報
              </h4>
              <div className="space-y-5">
                {[
                  { label: '会社名', value: '株式会社おかやま工務店' },
                  { label: '代表取締役', value: '岡山太郎' },
                  { label: '所在地', value: '〒700-0000 岡山市北区○○町1-2-3' },
                  { label: '電話番号', value: '086-012-3456' },
                  { label: 'FAX', value: '086-012-3457' },
                  { label: '設立', value: '2016年' },
                  { label: '営業時間', value: '8:00 〜 17:00' },
                  { label: '定休日', value: '日曜日・祝日' },
                  { label: '許可・資格', value: '建設業許可（般-○○）第○○○○○号\n一級建築士事務所登録\n岡山県知事許可' },
                ].map((info, index) => (
                  <div key={index} className="flex flex-col sm:flex-row border-b border-gray-200 pb-4">
                    <dt className="w-full sm:w-1/3 text-sm font-bold text-gray-600 mb-1 sm:mb-0">
                      {info.label}
                    </dt>
                    <dd className="w-full sm:w-2/3 text-base text-[#1a1a2e] whitespace-pre-line">
                      {info.value}
                    </dd>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: President Message */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h4 className="text-2xl lg:text-3xl font-black text-[#1a1a2e] mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                代表挨拶
              </h4>
              <div className="mb-8">
                <div className="w-40 h-40 mx-auto rounded-full overflow-hidden shadow-xl mb-5">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/d6420dc02e6c963d622b386e4a4d126e.webp"
                    alt="代表取締役 岡山太郎"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-center text-base font-bold text-[#1a1a2e] mb-1">代表取締役</p>
                <p className="text-center text-2xl font-black text-[#1a1a2e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  岡山 太郎
                </p>
              </div>
              <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                <p>株式会社おかやま工務店のホームページをご覧いただき、誠にありがとうございます。</p>
                <p>私は大工として40年以上、岡山市を中心に住宅の修理や内装・外装工事に携わってまいりました。長年の経験を通じて学んだことは、お客様一人ひとりの住まいへの想いを大切にし、確かな技術で応えることの重要性です。</p>
                <p>どんな小さな修理でも、お客様にとっては大切な住まいの一部です。私たちは、お客様の立場に立ち、丁寧な説明と誠実な対応を心がけております。</p>
                <p>これからも地域に根ざした工務店として、お客様の快適な暮らしを支えてまいります。住まいのことで何かお困りのことがございましたら、どうぞお気軽にご相談ください。</p>
              </div>
            </div>
          </div>

          {/* Map Section */}
          <div className="mt-12 lg:mt-16">
            <h4 className="text-2xl lg:text-3xl font-black text-[#1a1a2e] mb-8 text-center" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              アクセス
            </h4>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64842229488!2d133.89234!3d34.66167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355402e2a907e6e1%3A0x53ab4e8e7d00e31e!2z5bKh5bGx5biC5YyX5Yy6!5e0!3m2!1sja!2sjp!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="株式会社おかやま工務店の地図"
                ></iframe>
              </div>
              <div className="p-8">
                <h5 className="text-xl font-bold text-[#1a1a2e] mb-4">株式会社おかやま工務店</h5>
                <div className="space-y-2 text-gray-700">
                  <p className="flex items-start">
                    <i className="ri-map-pin-line text-[#ff6b35] text-xl mr-2 mt-1 flex-shrink-0"></i>
                    <span>〒700-0000 岡山市北区○○町1-2-3</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-phone-line text-[#ff6b35] text-xl mr-2 mt-1 flex-shrink-0"></i>
                    <span>TEL: 086-012-3456 / FAX: 086-012-3457</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-time-line text-[#ff6b35] text-xl mr-2 mt-1 flex-shrink-0"></i>
                    <span>営業時間: 8:00 〜 17:00（定休日: 日曜日・祝日）</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - お問い合わせ */}
      <section id="contact-section" className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              お問い合わせ
            </h3>
            <div className="w-24 h-1 bg-[#ff6b35] mx-auto rounded-full"></div>
            <p className="mt-6 text-base text-gray-600">お気軽にご相談ください</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <form id="contact-form-home" onSubmit={handleContactSubmit} data-readdy-form className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="home-name" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="home-name"
                    name="name"
                    value={contactFormData.name}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="山田太郎"
                  />
                </div>

                {/* Furigana */}
                <div>
                  <label htmlFor="home-furigana" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    フリガナ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="home-furigana"
                    name="furigana"
                    value={contactFormData.furigana}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="ヤマダタロウ"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="home-phone" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="home-phone"
                    name="phone"
                    value={contactFormData.phone}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="home-email" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="home-email"
                    name="email"
                    value={contactFormData.email}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                {/* Postal Code */}
                <div>
                  <label htmlFor="home-postalCode" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    郵便番号
                  </label>
                  <input
                    type="text"
                    id="home-postalCode"
                    name="postalCode"
                    value={contactFormData.postalCode}
                    onChange={handleContactChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="700-0000"
                  />
                </div>

                {/* Address */}
                <div>
                  <label htmlFor="home-address" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    ご住所
                  </label>
                  <input
                    type="text"
                    id="home-address"
                    name="address"
                    value={contactFormData.address}
                    onChange={handleContactChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm"
                    placeholder="岡山市北区○○町1-2-3"
                  />
                </div>

                {/* Inquiry Type */}
                <div>
                  <label htmlFor="home-inquiryType" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="home-inquiryType"
                    name="inquiryType"
                    value={contactFormData.inquiryType}
                    onChange={handleContactChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="外装工事について">外装工事について</option>
                    <option value="内装工事について">内装工事について</option>
                    <option value="リフォームについて">リフォームについて</option>
                    <option value="メンテナンスについて">メンテナンスについて</option>
                    <option value="見積もり依頼">見積もり依頼</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="home-message" className="block text-sm font-bold text-[#1a1a2e] mb-2">
                    ご相談内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="home-message"
                    name="message"
                    value={contactFormData.message}
                    onChange={handleContactChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#ff6b35] focus:outline-none transition-colors text-sm resize-none"
                    placeholder="ご相談内容を詳しくお書きください（500文字以内）"
                  ></textarea>
                  <p className="text-xs text-gray-600 mt-1 text-right">
                    {contactFormData.message.length} / 500文字
                  </p>
                </div>

                {/* Agreement */}
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="home-agree"
                    name="agree"
                    checked={contactFormData.agree}
                    onChange={handleContactChange}
                    required
                    className="mt-1 w-5 h-5 text-[#ff6b35] border-gray-300 rounded focus:ring-[#ff6b35] cursor-pointer"
                  />
                  <label htmlFor="home-agree" className="ml-3 text-sm text-gray-700 cursor-pointer">
                    個人情報の取り扱いに同意する <span className="text-red-500">*</span>
                  </label>
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={isContactSubmitting}
                    className="w-full px-8 py-4 bg-[#ff6b35] text-white text-lg font-bold rounded-full hover:bg-[#ff8555] transition-all duration-300 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isContactSubmitting ? '送信中...' : '送信する'}
                  </button>
                </div>

                {/* Status Messages */}
                {contactSubmitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg">
                    <p className="text-green-700 font-bold text-center">
                      お問い合わせありがとうございます。<br />
                      内容を確認次第、ご連絡させていただきます。
                    </p>
                  </div>
                )}
                {contactSubmitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg">
                    <p className="text-red-700 font-bold text-center">
                      送信に失敗しました。<br />
                      お手数ですが、お電話にてお問い合わせください。
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info Card */}
            <div className="lg:col-span-1">
              <div className="bg-[#1a1a2e] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">お急ぎの方はお電話で</h3>
                <a
                  href="tel:086-012-3456"
                  className="block text-4xl font-black mb-6 hover:text-[#ff6b35] transition-colors"
                >
                  086-012-3456
                </a>
                <div className="space-y-3 text-sm text-gray-300">
                  <p className="flex items-start">
                    <i className="ri-time-line text-[#ff6b35] text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                    <span>営業時間: 8:00 〜 17:00</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-calendar-line text-[#ff6b35] text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                    <span>定休日: 日曜日・祝日</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-map-pin-line text-[#ff6b35] text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                    <span>岡山市北区○○町1-2-3</span>
                  </p>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-700">
                  <p className="text-sm leading-relaxed">
                    お見積もりは無料です。<br />
                    どんな小さなことでも<br />
                    お気軽にご相談ください。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - お困りごと相談 */}
      <section className="py-16 lg:py-24 px-6 bg-[#faf8f3]">
        <div className="max-w-[1400px] mx-auto text-center">
          <h3 className="text-3xl sm:text-4xl lg:text-6xl font-black text-[#1a1a2e] leading-tight mb-10" style={{ fontFamily: "'Noto Sans JP', sans-serif", lineHeight: '1.2' }}>
            お家のことで困ったら、<br />まずはご相談ください。
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:086-012-3456"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#ff6b35] text-white text-base sm:text-lg font-bold rounded-full hover:bg-[#ff8555] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2"></i>
              今すぐ電話で相談
            </a>
            <a
              href="#contact-section"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('contact-section');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-[#1a1a2e] text-[#1a1a2e] text-base sm:text-lg font-bold rounded-full hover:bg-[#1a1a2e] hover:text-white transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              お問い合わせフォーム
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;